import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { callSheetApi } from "@/lib/api";
import {
  Loader2,
  MapPin,
  Car,
  Building2,
  Phone,
  Mail,
  Clock,
  Cloud,
  Sunrise,
  Sunset,
  CalendarDays,
  User,
  Clapperboard,
  AlertCircle,
  Navigation,
} from "lucide-react";

export default function PublicCallSheetPage() {
  const { csId } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        const data = await callSheetApi.publicGet(csId);
        setState({ loading: false, data });
      } catch (e) {
        setState({
          loading: false,
          error: e?.response?.data?.detail || "Call sheet not found",
        });
      }
    })();
  }, [csId]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-7 h-7 animate-spin text-amber-500" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-6">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
          <h1 className="font-heading text-2xl text-zinc-100 mb-2">
            Call sheet unavailable
          </h1>
          <p className="text-zinc-500 text-sm">{state.error}</p>
        </div>
      </div>
    );
  }

  return <PublicView {...state.data} />;
}

function PublicView({ call_sheet: cs, project, locations, team, days }) {
  const teamById = useMemo(
    () => Object.fromEntries(team.map((m) => [m.id, m])),
    [team]
  );

  const talent = (cs.talent || [])
    .map((t) => ({ ...t, person: teamById[t.team_id] }))
    .filter((t) => t.person);

  const clients = (cs.clients || [])
    .map((c) => ({ ...c, person: teamById[c.team_id] }))
    .filter((c) => c.person);

  const crewGrouped = useMemo(() => {
    const order = [
      "PRODUCTION", "CAMERA", "LIGHTING", "GRIP", "SOUND",
      "ART", "WARDROBE", "HAIR & MAKEUP", "POST", "OTHER",
    ];
    const map = {};
    (cs.crew || []).forEach((c) => {
      const m = teamById[c.team_id];
      if (!m) return;
      const dept = m.department || "OTHER";
      if (!map[dept]) map[dept] = [];
      map[dept].push({ ...c, person: m });
    });
    const result = [];
    order.forEach((d) => map[d] && result.push([d, map[d]]));
    Object.keys(map).forEach((d) => {
      if (!order.includes(d)) result.push([d, map[d]]);
    });
    return result;
  }, [cs.crew, teamById]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative z-10">
      {/* Brand header */}
      <header className="bg-zinc-950 border-b border-zinc-800/70 px-5 py-4 sticky top-0 z-20 backdrop-blur">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
            alt="Whiplash"
            className="h-7"
          />
          <span className="text-amber-500 text-[9px] font-semibold tracking-[0.3em] uppercase">
            Call Sheet
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 pb-16">
        {/* Title */}
        <section className="mb-6 pb-5 border-b border-zinc-800">
          <h1 className="font-heading text-3xl sm:text-4xl font-light text-zinc-100 leading-tight">
            {project.name}
          </h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {project.job_number && (
              <span
                data-testid="public-job-number"
                className="inline-block text-[10px] font-mono font-bold tracking-wider uppercase px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/40"
              >
                Job #{project.job_number}
              </span>
            )}
            {(days && days.length > 1) && (
              <span
                data-testid="public-day-badge"
                className="inline-block text-[10px] font-mono font-bold tracking-wider uppercase px-2 py-0.5 rounded-sm bg-zinc-800 text-zinc-200 border border-zinc-700"
              >
                {cs.day_label?.trim() || `Day ${cs.day_number}`} of {days.length}
              </span>
            )}
            {cs.day_of && (
              <div className="text-overline text-amber-500">{cs.day_of}</div>
            )}
          </div>
          {cs.shoot_date && (
            <div className="flex items-center gap-2 text-zinc-400 text-sm mt-3">
              <CalendarDays className="w-4 h-4" />
              {fmtDate(cs.shoot_date)}
            </div>
          )}

          {/* Other days picker — small horizontal list */}
          {days && days.length > 1 && (
            <div className="mt-4 flex items-center gap-1.5 flex-wrap">
              <span className="text-overline text-zinc-500 mr-1">Other days</span>
              {days
                .filter((d) => d.id !== cs.id)
                .map((d) => (
                  <a
                    key={d.id}
                    href={`/share/call-sheet/${d.id}`}
                    data-testid={`other-day-link-${d.day_number}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-sm border border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-amber-500/60 hover:text-amber-400 transition-colors"
                  >
                    <span>{d.day_label?.trim() || `Day ${d.day_number}`}</span>
                    {d.shoot_date && (
                      <span className="text-zinc-500 font-mono">
                        {fmtDayShort(d.shoot_date)}
                      </span>
                    )}
                  </a>
                ))}
            </div>
          )}
        </section>

        {/* Key times — large for mobile */}
        <section className="grid grid-cols-2 gap-3 mb-6">
          <BigKV label="General Call" value={fmtTime(cs.general_call)} amber />
          <BigKV label="Shooting Call" value={fmtTime(cs.shooting_call)} />
          <BigKV label="Est. Wrap" value={fmtTime(cs.est_wrap)} />
          <BigKV label="Weather" value={cs.weather} icon={Cloud} />
        </section>

        {(cs.sunrise || cs.sunset || cs.director_contact) && (
          <section className="grid grid-cols-2 gap-3 mb-8 text-xs">
            {cs.sunrise && (
              <Inline icon={Sunrise} label="Sunrise" value={cs.sunrise} />
            )}
            {cs.sunset && (
              <Inline icon={Sunset} label="Sunset" value={cs.sunset} />
            )}
            {cs.director_contact && (
              <div className="col-span-2 flex items-center gap-2 text-zinc-400">
                <User className="w-3.5 h-3.5 text-amber-500/70" />
                <span className="text-zinc-500">Director:</span>
                <span className="text-zinc-200">{cs.director_contact}</span>
              </div>
            )}
          </section>
        )}

        {/* Locations */}
        {locations.length > 0 && (
          <section className="mb-8">
            <SectionTitle>Locations</SectionTitle>
            <div className="space-y-3">
              {locations.map((loc) => (
                <LocationCard key={loc.id} loc={loc} />
              ))}
            </div>
          </section>
        )}

        {/* Hospitals */}
        {(() => {
          const hospitals = locations
            .map((l) => l.nearest_hospital)
            .filter((h) => h && h.trim());
          if (hospitals.length === 0) return null;
          const unique = [...new Set(hospitals)];
          return (
            <section className="mb-8">
              <SectionTitle>Nearest Hospital</SectionTitle>
              <div className="space-y-2">
                {unique.map((h, i) => (
                  <a
                    key={i}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`hospital-link-${i}`}
                    className="block rounded-md bg-rose-500/5 border border-rose-500/20 hover:border-rose-500/50 p-4 transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-overline text-rose-400/80 mb-1">
                          Emergency
                        </div>
                        <div className="text-sm text-zinc-100 group-hover:text-rose-300 leading-snug">
                          {h}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
                          Tap for directions
                          <Navigation className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          );
        })()}

        {/* Talent */}
        {talent.length > 0 && (
          <section className="mb-8">
            <SectionTitle>Talent</SectionTitle>
            <div className="space-y-2">
              {talent.map((t) => (
                <PersonCard
                  key={t.team_id}
                  person={t.person}
                  callTime={t.call_time}
                  extra={[
                    t.status && `Status: ${t.status}`,
                    t.pickup && `Pickup: ${t.pickup}`,
                    t.hmu && `H/MU: ${t.hmu}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                />
              ))}
            </div>
          </section>
        )}

        {/* Crew */}
        {crewGrouped.length > 0 && (
          <section className="mb-8">
            <SectionTitle>Crew</SectionTitle>
            {crewGrouped.map(([dept, items]) => (
              <div key={dept} className="mb-5">
                <div className="text-overline text-zinc-500 mb-2">{dept}</div>
                <div className="space-y-2">
                  {items.map((c) => (
                    <PersonCard
                      key={c.team_id}
                      person={c.person}
                      callTime={c.call_time}
                    />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Client */}
        {clients.length > 0 && (
          <section className="mb-8" data-testid="public-client-section">
            <SectionTitle>Client</SectionTitle>
            <div className="space-y-2">
              {clients.map((c) => (
                <PersonCard
                  key={c.team_id}
                  person={c.person}
                  callTime={c.call_time}
                  hideContacts
                  subtitle={
                    [c.person.role, c.person.company].filter(Boolean).join(" · ") ||
                    "Client"
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* Notes */}
        {cs.production_notes && (
          <section className="mb-8">
            <SectionTitle>Production Notes</SectionTitle>
            <div className="bg-zinc-900 border-l-2 border-amber-500 rounded-sm p-4 text-sm text-zinc-200 whitespace-pre-line leading-relaxed">
              {cs.production_notes}
            </div>
          </section>
        )}

        <footer className="pt-6 border-t border-zinc-800/60 text-center">
          <Clapperboard className="w-4 h-4 text-amber-500/70 mx-auto mb-2" />
          <div className="text-overline text-zinc-600">
            Whiplash Film &amp; Creative
          </div>
        </footer>
      </main>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-overline text-zinc-400 mb-3 pb-2 border-b border-zinc-800/60">
      {children}
    </div>
  );
}

function BigKV({ label, value, amber, icon: Icon }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-3">
      <div className="text-overline text-zinc-500 mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`font-heading text-xl ${amber ? "text-amber-500" : "text-zinc-100"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function Inline({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 text-zinc-400">
      <Icon className="w-3.5 h-3.5 text-amber-500/70" />
      <span className="text-zinc-500">{label}:</span>
      <span className="text-zinc-200 font-mono">{value}</span>
    </div>
  );
}

function LocationCard({ loc }) {
  const mapHref = loc.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`
    : null;
  return (
    <div className="rounded-md bg-zinc-900 border border-zinc-800 border-l-2 border-l-amber-500 p-4">
      <div className="font-heading text-lg text-zinc-100 mb-3">{loc.name}</div>
      {loc.address && (
        <a
          href={mapHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`public-location-address-${loc.id}`}
          className="flex items-start gap-2 text-sm text-zinc-300 hover:text-amber-500 mb-2 group"
        >
          <MapPin className="w-4 h-4 mt-0.5 text-amber-500/70 shrink-0" />
          <span className="leading-snug">
            {loc.address}
            <Navigation className="inline w-3 h-3 ml-1.5 text-zinc-500 group-hover:text-amber-500" />
          </span>
        </a>
      )}
      {loc.parking_info && (
        <div className="flex items-start gap-2 text-xs text-zinc-400 mb-2">
          <Car className="w-3.5 h-3.5 mt-0.5 shrink-0 text-zinc-500" />
          <span>{loc.parking_info}</span>
        </div>
      )}
      {(loc.contact_name || loc.contact_phone) && (
        <div className="pt-3 mt-2 border-t border-zinc-800 flex items-center gap-3 text-xs flex-wrap">
          {loc.contact_name && (
            <span className="text-zinc-400">{loc.contact_name}</span>
          )}
          {loc.contact_phone && (
            <a
              href={`tel:${loc.contact_phone}`}
              className="inline-flex items-center gap-1 text-amber-500 font-mono"
            >
              <Phone className="w-3 h-3" /> {loc.contact_phone}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function fmtTime(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{1,2}:\d{2}\s?(am|pm)$/i.test(s)) return s.toLowerCase().replace(/\s/g, "");
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const ampm = h < 12 ? "am" : "pm";
    const h12 = h % 12 || 12;
    return `${h12}:${m[2]}${ampm}`;
  }
  return s;
}

function fmtDayShort(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function PersonCard({ person, callTime, extra, hideContacts, subtitle }) {
  return (
    <div className="rounded-sm bg-zinc-900 border border-zinc-800 p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-xs font-semibold shrink-0">
        {(person.name || "")
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((w) => w[0]?.toUpperCase())
          .join("")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate">{person.name}</div>
        <div className="text-xs text-zinc-500 truncate">
          {subtitle || person.role || person.department}
        </div>
        {extra && (
          <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{extra}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {callTime && (
          <div className="flex items-center gap-1 text-amber-500 font-semibold text-sm">
            <Clock className="w-3.5 h-3.5" />
            {fmtTime(callTime)}
          </div>
        )}
        {!hideContacts && (
          <div className="flex items-center gap-2">
            {person.phone && (
              <a
                href={`tel:${person.phone}`}
                data-testid={`call-${person.id}`}
                title="Call"
                className="p-1.5 rounded-full bg-zinc-800 hover:bg-amber-500 hover:text-black text-zinc-400 transition-colors"
              >
                <Phone className="w-3 h-3" />
              </a>
            )}
            {person.email && (
              <a
                href={`mailto:${person.email}`}
                data-testid={`email-${person.id}`}
                title="Email"
                className="p-1.5 rounded-full bg-zinc-800 hover:bg-amber-500 hover:text-black text-zinc-400 transition-colors"
              >
                <Mail className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
